import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { ArrowRight, AlertCircle, ShieldCheck, Copy, Check } from 'lucide-react';
import { BRAND } from '../data/mockData';

const AdminLogin = () => {
  const { user, login, verify2fa } = useAuth();
  const location = useLocation();

  // Stage machine: 'password' → 'twofa' | 'twofa_setup'
  const [stage, setStage] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState(null); // { challenge_token, status, setup? }
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [acknowledgedCodes, setAcknowledgedCodes] = useState(false);

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0F0F11] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    const from = location.state?.from || '/admin';
    return <Navigate to={from} replace />;
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.twofa) {
        const { status } = result.twofa;
        setChallenge(result.twofa);
        setStage(status === '2fa_setup_required' ? 'twofa_setup' : 'twofa');
        setCode('');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (stage === 'twofa_setup' && !acknowledgedCodes) {
      setError('Please save your backup codes and confirm before continuing.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await verify2fa(challenge.challenge_token, code, trustDevice);
      // AuthContext sets user → redirect happens via the top-level guard.
    } catch (err) {
      setError(err.message || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = async () => {
    if (!challenge?.setup?.backup_codes) return;
    try {
      await navigator.clipboard.writeText(
        challenge.setup.backup_codes.join('\n')
      );
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2500);
    } catch (_) {
      /* clipboard may be blocked in sandboxed frames — user can still copy manually */
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F0F11] flex items-center justify-center p-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img
            src={BRAND.logoFull}
            alt="Grouth"
            className="h-12 w-auto brightness-0 invert mx-auto mb-3"
          />
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#83BF42] font-['Inter']">
            Admin Console
          </p>
        </div>

        {/* Stage 1 — password */}
        {stage === 'password' && (
          <form
            onSubmit={handlePasswordSubmit}
            data-testid="admin-login-form"
            className="bg-[#15151A] rounded-2xl p-8 border border-white/5 shadow-2xl"
          >
            <h1 className="text-white text-2xl font-light font-['Inter'] mb-2">Welcome back</h1>
            <p className="text-white/40 text-sm font-['Lato'] mb-8">Sign in to manage the site.</p>

            {error && (
              <div
                data-testid="admin-login-error"
                className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <label className="block text-xs uppercase tracking-wider text-white/50 font-['Inter'] mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              data-testid="admin-login-email"
              className="w-full px-4 py-3 mb-5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] focus:outline-none focus:border-[#83BF42] transition-colors"
              placeholder="admin@grouth.co"
            />

            <label className="block text-xs uppercase tracking-wider text-white/50 font-['Inter'] mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
              className="w-full px-4 py-3 mb-7 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] focus:outline-none focus:border-[#83BF42] transition-colors"
              placeholder="••••••••"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition-all duration-300 group disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
        )}

        {/* Stage 2A — existing 2FA user, enter code */}
        {stage === 'twofa' && (
          <form
            onSubmit={handleCodeSubmit}
            data-testid="admin-2fa-form"
            className="bg-[#15151A] rounded-2xl p-8 border border-white/5 shadow-2xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-[#83BF42]" />
              <h1 className="text-white text-2xl font-light font-['Inter']">Two-factor code</h1>
            </div>
            <p className="text-white/40 text-sm font-['Lato'] mb-8">
              Enter the 6-digit code from your authenticator app, or one of your backup codes.
            </p>

            {error && (
              <div
                data-testid="admin-2fa-error"
                className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <label className="block text-xs uppercase tracking-wider text-white/50 font-['Inter'] mb-2">
              Verification code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              required
              data-testid="admin-2fa-code"
              className="w-full px-4 py-3 mb-4 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono tracking-wider focus:outline-none focus:border-[#83BF42] transition-colors"
              placeholder="123456 or XXXX-XXXX"
            />

            <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer mb-6 select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                data-testid="admin-2fa-trust-device"
                className="mt-0.5 accent-[#83BF42]"
              />
              <span>
                Trust this browser for 30 days.
                <span className="block text-white/40 mt-0.5">
                  Skip the 2FA code on <strong className="text-white/60">this browser only</strong>. Don’t tick on shared computers.
                </span>
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-2fa-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify & continue'}
            </button>

            <button
              type="button"
              onClick={() => { setStage('password'); setChallenge(null); setCode(''); setError(''); }}
              data-testid="admin-2fa-back"
              className="w-full mt-3 text-xs text-white/40 hover:text-white/70 font-['Inter'] transition-colors"
            >
              ← Use a different account
            </button>
          </form>
        )}

        {/* Stage 2B — first-time enrolment */}
        {stage === 'twofa_setup' && challenge?.setup && (
          <form
            onSubmit={handleCodeSubmit}
            data-testid="admin-2fa-setup-form"
            className="bg-[#15151A] rounded-2xl p-8 border border-white/5 shadow-2xl"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-[#83BF42]" />
              <h1 className="text-white text-2xl font-light font-['Inter']">Set up 2FA</h1>
            </div>
            <p className="text-white/50 text-sm font-['Lato'] mb-6">
              Two-factor authentication is required. Scan the QR with any authenticator app
              (Google Authenticator, Authy, 1Password, Microsoft Authenticator, Bitwarden…).
            </p>

            {error && (
              <div
                data-testid="admin-2fa-setup-error"
                className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="bg-white rounded-xl p-4 flex items-center justify-center mb-5">
              <img
                src={challenge.setup.qr_data_url}
                alt="2FA QR code"
                data-testid="admin-2fa-qr"
                className="w-48 h-48"
              />
            </div>

            <div className="mb-5 text-center">
              <p className="text-[11px] uppercase tracking-wider text-white/40 font-['Inter'] mb-1">
                Can’t scan? Enter this secret manually
              </p>
              <p
                data-testid="admin-2fa-secret"
                className="text-white/80 text-sm font-mono tracking-wider break-all"
              >
                {challenge.setup.secret}
              </p>
            </div>

            <div className="mb-6 p-4 rounded-lg bg-[#83BF42]/5 border border-[#83BF42]/20">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-wider text-[#83BF42] font-['Inter'] font-semibold">
                  Backup codes (save these now)
                </p>
                <button
                  type="button"
                  onClick={copyBackupCodes}
                  data-testid="admin-2fa-copy-codes"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] text-white/70 hover:text-white bg-white/5 hover:bg-white/10 transition-colors"
                >
                  {copiedCodes ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedCodes ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div
                data-testid="admin-2fa-backup-codes"
                className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm text-white/80"
              >
                {challenge.setup.backup_codes.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-white/40 font-['Inter']">
                Each code works exactly once. Store them somewhere safe — you’ll need them if you lose your phone.
              </p>
              <label className="mt-3 flex items-start gap-2 text-xs text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgedCodes}
                  onChange={(e) => setAcknowledgedCodes(e.target.checked)}
                  data-testid="admin-2fa-ack"
                  className="mt-0.5 accent-[#83BF42]"
                />
                I’ve saved my backup codes somewhere safe.
              </label>
            </div>

            <label className="block text-xs uppercase tracking-wider text-white/50 font-['Inter'] mb-2">
              Enter the 6-digit code from your app to finish setup
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              data-testid="admin-2fa-setup-code"
              className="w-full px-4 py-3 mb-4 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono tracking-widest text-center text-lg focus:outline-none focus:border-[#83BF42] transition-colors"
              placeholder="123456"
            />

            <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer mb-6 select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                data-testid="admin-2fa-setup-trust-device"
                className="mt-0.5 accent-[#83BF42]"
              />
              <span>
                Trust this browser for 30 days after finishing setup.
                <span className="block text-white/40 mt-0.5">
                  On future logins from <strong className="text-white/60">this browser only</strong> you’ll skip the code step.
                </span>
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !acknowledgedCodes}
              data-testid="admin-2fa-setup-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying…' : 'Finish setup & sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
